import { Component, EventEmitter, OnInit, Output, Input } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { OrganisationLabelTags } from '@sb-shared/constants/organisation-label-tags.constants';
import { AvatarButtonAction } from '@sb-shared/constants/shared.constant';
import { RoleId } from '@sb-shared/enums/role.enum';
import { LegacyReceptionPersonCreateUpdate } from '@sb-shared/models/legacy-reception-person-create-update';
import { Organisation } from '@sb-shared/models/organisation';
import { Button } from '@sb-shared/models/UI/buttons';
import { WizardTab } from '@sb-shared/models/UI/wizard-tabs';
import { User } from '@sb-shared/models/user';
import { UserUi } from '@sb-shared/models/user-ui';
import { ConfirmDialogService } from '@sb-shared/services/confirm-dialog.service';
import { OrganisationService } from '@sb-shared/services/organisation.service';
import { UserService } from '@sb-shared/services/user.service';
import { finalize, forkJoin, Observable, of, Subscription, switchMap } from 'rxjs';
import { MessagingService } from 'src/app/messaging/services/messaging.service';
import { ReceptionService } from 'src/app/reception/services/reception.service';

import { ProfileDataService } from '../../services/profile-data.service';
import { ProfileWizardDataService } from '../../services/profile-wizard-data.service';

@Component({
    selector: 'sb-profile-profile',
    templateUrl: './profile-profile.component.html',
    styleUrls: ['./profile-profile.component.scss'],
    standalone: false
})
export class ProfileProfileComponent implements OnInit {
  isOrganisationAdmin: boolean;
  isOrganisationSuperAdmin: boolean;
  editUserTabs: WizardTab[];
  selectedPerson: User;
  deleteUserButton: Button;
  messageUserButton: Button;
  isSaving = false;
  isLocked = true;
  currentUser: UserUi;
  currentOrganisation: Organisation;
  currentOrganisationIsSchool: boolean;
  currentUserOrganisations: string[];
  labels = {
    organisations: OrganisationLabelTags.Organisations
  };

  readonly AvatarButtonAction = AvatarButtonAction;

  @Output() onUserUpdated: EventEmitter<void> = new EventEmitter();
  userInContext$: Subscription;

  constructor(
    private profileDataService: ProfileDataService,
    private profileWizardDataService: ProfileWizardDataService,
    private receptionService: ReceptionService,
    private confirmDialogService: ConfirmDialogService,
    private translateService: TranslateService,
    private messagingService: MessagingService,
    private organisationService: OrganisationService,
    private userService: UserService
  ) { }

  ngOnInit(): void {
    forkJoin([
      this.organisationService.getCurrentOrganisation(),
      this.userService.getCurrentUser()
    ]).subscribe(([organisation, user]: [Organisation, UserUi]) => {
      this.currentOrganisation = organisation;
      this.currentOrganisationIsSchool = this.organisationService.isSchool(organisation);
      this.isOrganisationAdmin = this.userService.checkUserRole(user, RoleId.Admin);
      this.isOrganisationSuperAdmin = this.userService.checkUserRole(user, RoleId.OrganisationSuperAdmin);
      this.currentUser = user;
      this.currentUserOrganisations = user.organisations;

      this.userInContext$ = this.profileDataService.getUserInContext()
        .subscribe(user => {
          if (!user) {
            return;
          }

          this.selectedPerson = user;

          this.updateButtons();

          this.isLocked = this.showLocked();

          this.editUserTabs = this.profileWizardDataService.getWizardTabs(
            this.currentOrganisation, 
            this.currentUser, 
            this.selectedPerson);
        });
    });
  }

  ngOnDestroy(): void {
    this.userInContext$.unsubscribe();
  }

  saveUser(user: User) {
    this.isSaving = true;
    const isOwnProfile = this.currentUser.id === user.id;

    let saveStaffInformation$: Observable<unknown> = of(null);

    // Staff information is written by its own super-admin endpoint, because SavePerson does not
    // carry the cost. A super admin may set it on a colleague or on their own record.
    if (this.isOrganisationSuperAdmin && user.staffInformation) {
      const { staffInformation: { onlineMeetingId, staffCostTypeId, staffCost }, id: staffPersonId } = user;

      saveStaffInformation$ = this.saveStaffInformationFromSuperAdmins({ onlineMeetingId, staffPersonId, staffCostTypeId, staffCost });

      // CanUpdateProfileAsync refuses a staff target outright, and refuses one's own profile too
      // once it has been seen in an MIS - every Inspired user - so in both cases the
      // staff-information write is the entire save and calling SavePerson would only raise a
      // spurious "Person not editable" alongside it. The fields stay editable on a locked
      // profile via ignoreReadOnly, as the online meeting id already does.
      if (!isOwnProfile || !this.isEditable()) {
        saveStaffInformation$
          .pipe(finalize(() => this.isSaving = false))
          .subscribe();
        return;
      }
    }

    // On an editable self-edit SavePerson also upserts StaffInformation, so it must run after
    // the staff-information write: run concurrently, both can find no row and both INSERT, and
    // there is no unique constraint on (OrganisationId, PersonId) to stop the duplicate.
    saveStaffInformation$
      .pipe(switchMap(() => this.receptionService.savePerson(this.getLegacyReceptionPersonCreateUpdate(user))))
      .subscribe({
        next: (data) => {
          setTimeout(() => {
            this.isSaving = false;
            if (data) {
              this.profileDataService.setSelectedPersonById(this.selectedPerson.personData.id);
              this.onUserUpdated.emit();
            }
          });
        },
        // A rejected staff-information write (e.g. a negative cost) skips SavePerson.
        error: () => this.isSaving = false
      });
  }

  deleteUserButtonClick() {
    const selectedPerson = this.selectedPerson;
    const isDeleted = selectedPerson.personStatus === 'SB_Deleted';
    const actionLabel = isDeleted ? 'SB_Restore' : 'SB_Delete';
    this.confirmDialogService.confirmThis({
      title: actionLabel,
      text:
        `${this.translateService.instant('SB_Please_Confirm_That_You_Wish_To_X', { Markup: this.translateService.instant(actionLabel) })}
       ${selectedPerson.personData.firstName} ${selectedPerson.personData.lastName}.`,
      isDanger: true,
      warningText: isDeleted ? 'SB_Restore_User_Links_Info' : ''
    }, () => {
      if (isDeleted) {
        this.receptionService.restorePerson(selectedPerson.personData.id, true)
          .subscribe(() => {
            this.profileDataService.setSelectedPersonById(this.selectedPerson.personData.id);
            this.onUserUpdated.emit();
          })

      }
      else {
        this.receptionService.deletePerson(this.selectedPerson.personData.id)
          .subscribe(() => {
            this.profileDataService.setSelectedPersonById(this.selectedPerson.personData.id);
            this.onUserUpdated.emit();
          })
      }
    }, () => null)
  }

  sendSingleAccessRequestEmail() {
    this.messageUserButton.isLoading = true;
    this.messagingService.sendSingleAccessRequestEmail(this.selectedPerson)
      .subscribe()
      .add(() => {
        this.messageUserButton = {
          isLoading: false,
          isDisabled: true,
          message: 'SB_Sent'
        };
      });
  }

  showLocked() {
    return this.isEditable() && this.selectedPerson.seenInMIS;
  }

  isReadOnly() {
    return !this.isEditable() || this.isLocked;
  }

  // Editable if:
  // - either:
  //    - viewing ourselves, not seen in MIS and is staff
  //    - clubs only, not seen in MIS and either parent viewing themselves or linked student
  //    - current user is super admin and user is student or parent (not staff parent)
  isEditable() {
    if (!this.selectedPerson) {
      return false;
    }

    if (this.isViewingSelf() && // viewing ourselves...
      !this.selectedPerson.seenInMIS && // ...not seen in MIS...
      this.selectedPerson.isStaff) { // ...and is staff
      return true;
    }

    if (!this.currentOrganisationIsSchool && // clubs...
      !this.selectedPerson.seenInMIS && // ...not seen in MIS and either...
      ((this.isViewingSelf() && this.selectedPerson.isParent) || // ...parent viewing themselves or...
        this.isViewingChildLinkedToSelf())) { // ...parent viewing linked student.
      return true;
    }

    return this.isOrganisationSuperAdmin && // super admin can edit...
      (this.selectedPerson.isParent || this.selectedPerson.isPupil && // parents and pupils...
      !this.selectedPerson.isDeleted) && // ...not deleted...
      !this.selectedPerson.isStaff && // but not staff parents...
      !this.selectedPerson.isExternal; // or to be sure, not external.
  }

  getLegacyReceptionPersonCreateUpdate(user: User): LegacyReceptionPersonCreateUpdate {
    return {
      ...user.personData,
      emailAddress: user.emailAddress,
      id: user.id,
      isExternal: user.userType ? user.userType == 'isExternal' : user.isExternal,
      isExternalCoachAccess: user.isExternalCoachAccess,
      isParent: user.isParent,
      isPupil: user.userType ? user.userType == 'isStudent' : user.isPupil,
      isStaff: user.userType ? user.userType == 'isStaff' : user.isStaff,
      isTransportBusMonitor: user.isTransportBusMonitor,
      misId: user.misId,
      attendanceScanningCode: user.pupilInformation?.attendanceScanningCode,
      onlineMeetingId: user.staffInformation?.onlineMeetingId,
      schoolYearGroupId: user.pupilInformation?.schoolYearGroupId,
      schoolClassId: user.pupilInformation?.schoolClassId,
      schoolHouseId: user.pupilInformation?.schoolHouseId
    };
  }

  isViewingSelf() {
    return this.selectedPerson?.id == this.currentUser.id;
  }

  isViewingChildLinkedToSelf() {
    return this.currentUser.children.some(child => child.id == this.selectedPerson?.id);
  }


  subHeadingLabel() {
    if (!this.selectedPerson) {
      return '';
    }

    if (this.isViewingSelf()) {
      return 'SB_Your_Information';
    }

    if (this.selectedPerson.isStaff) {
      return 'SB_Staff_Information';
    }

    if (this.selectedPerson.isExternal) {
      return 'SB_External_User_Information';
    }

    if (this.selectedPerson.isParent) {
      return 'SB_Parent_Information';
    }

    if (this.selectedPerson.isPupil) {
      return 'SB_Pupil_Information';
    }

    return '';
  }

  showUserStatus() {
    return this.selectedPerson?.personStatus === 'SB_Active' ||
      this.selectedPerson?.personStatus === 'SB_Inactive' ||
      this.selectedPerson?.personStatus === 'SB_Not_logged_in' ||
      this.selectedPerson?.personStatus === 'SB_Deleted';
  }

  private updateButtons() {
    if (this.profileDataService.isReception() && this.isOrganisationSuperAdmin && this.selectedPerson.personData?.id > 0 && !this.isViewingSelf()) {
      this.deleteUserButton = {
        message: this.selectedPerson.personStatus === 'SB_Deleted' ? 'SB_Restore' : 'SB_Delete'
      };
    }
    else {
      this.deleteUserButton = null;
    }

    if (this.profileDataService.isReception() && this.isOrganisationAdmin) {
      const activationLabel = this.messagingService.getActivationLabel(this.selectedPerson);
      this.messageUserButton = !activationLabel ? null //!set to null to hide the button, for deleted users
        : { message: activationLabel };
    }
  }

  /**
   * Save online meeting url to user, can ONLY used by a user with super admin role
   *
   * @private
   * @param {User} user
   * @memberof ProfileProfileComponent
   */
  private saveStaffInformationFromSuperAdmins({ staffPersonId, onlineMeetingId, staffCostTypeId, staffCost }): Observable<unknown> {
    if (!this.userService.checkUserRole(this.currentUser, RoleId.OrganisationSuperAdmin)) {
      return of(null);
    }

    return this.receptionService.updateStaffInformation({ onlineMeetingId, staffPersonId, staffCostTypeId, staffCost });
  }
}
